/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo } from 'react';

import Imgix from 'js/components/Imgix';
import { useRetracked } from 'js/lib/retracked';
import { get } from 'js/lib/user';

import { Link, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import ProgramCard, { LOGO_WIDTH } from 'bundles/composable-page-header/components/program-switcher/ProgramCard';
import type {
  DegreeForProgramSwitcher as Degree,
  EnterpriseProgramForProgramSwitcher as EnterpriseProgram,
} from 'bundles/composable-page-header/components/program-switcher/types';
import getByOrganizations from 'bundles/composable-page-header/utils/getByOrganizations';
import { sortDegreesByLaunchedDate } from 'bundles/composable-page-header/utils/sortByLaunchedAt';
import EnterpriseLogo from 'bundles/page-header/components/EnterpriseLogo';
import { redirectToMyCourseraAndUpdateUserProgram } from 'bundles/page-header/components/mobile/utils';
import { fillWithWhiteBackground } from 'bundles/page/common/imgixParams';

import CourseraLogo from 'images/page-header/coursera-logo-c-mark.png';

import _t from 'i18n!nls/composable-page-header';

export type Props = {
  programs: Array<EnterpriseProgram>;
  degrees: Array<Degree>;
  currentProgram?: EnterpriseProgram | null;
  currentDegree?: Degree | null;
  alwaysShowPersonalAccount?: boolean;
  trackingName?: string;
};

const styles = {
  list: css`
    list-style: none;
    padding-left: 0;
    padding-bottom: var(--cds-spacing-150);
  `,
  organization: css`
    border-top: 1px solid var(--cds-color-grey-50);
    list-style: none;
    margin-top: var(--cds-spacing-100);
  `,
  orgName: css`
    padding: var(--cds-spacing-200) var(--cds-spacing-100) var(--cds-spacing-100);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
  programWrapper: css`
    text-decoration: none;

    :active {
      text-decoration: none;
    }
  `,
  programCard: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex-shrink: 1;
  `,
  logo: css`
    width: ${LOGO_WIDTH}px;
    text-align: center;
  `,
  checkIcon: css`
    justify-self: end;
    flex-shrink: 0;
  `,
  viewAllLink: css`
    padding: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-100);
    display: block;

    &&& {
      color: var(--cds-color-neutral-primary);
    }

    /* override box-shadow from .cds-link-focusVisible */
    box-shadow: none !important;

    :hover {
      text-decoration: none;
      background-color: var(--cds-color-grey-25);
    }

    :active {
      text-decoration: none;
    }

    :focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: -2px;
      text-decoration: none;
    }
  `,
};

const ProgramList = ({
  programs: allPrograms,
  degrees,
  currentDegree,
  currentProgram,
  alwaysShowPersonalAccount,
  trackingName,
}: Props) => {
  const sortedDegrees = useMemo(() => sortDegreesByLaunchedDate(degrees), [degrees]);

  const organizations = useMemo(() => getByOrganizations(allPrograms), [allPrograms]);

  const retracked = useRetracked();
  const track = useTracker();

  const onHomepageClick = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((productId, homepageType) => {
    const previousHomepage = currentDegree?.id || currentProgram?.id || '';
    const previousHomepageType = currentDegree?.id ? 'degree' : currentProgram?.id ? 'enterprise_program' : 'consumer';

    track('switch_homepage', {
      previousHomepage,
      currentHomepage: productId || '',
      currentHomepageType: homepageType,
      previousHomepageType,
    });
    // this function does not need to re-run when the dependencies ('currentDegree' and 'currentProgram') change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectToMyCoursera = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((e, productId) => {
    e.preventDefault();
    onHomepageClick(productId, 'consumer');

    const userId = get().id;
    redirectToMyCourseraAndUpdateUserProgram({ userId });
    // Adding dependencies would re-run this function unnecessarily whenever 'onHomepageClick' updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!allPrograms.length && !degrees.length && !alwaysShowPersonalAccount) return null;

  return (
    <React.Fragment>
      {organizations.map((organization) => {
        const { name: orgName, slug: orgSlug, programs, id: orgId } = organization;
        const viewAllProgramHref = `/programs/all/${orgSlug}`;

        return (
          <li key={organization.id} css={styles.organization}>
            <Typography2 component="h3" variant="subtitleMedium" css={styles.orgName}>
              {orgName}
            </Typography2>
            <ul css={styles.list} role="none">
              {programs.map((program) => {
                const {
                  id: programId,
                  name: programName,
                  description: programDescription,
                  squareLogo: programSquareLogo,
                  slug: programSlug,
                } = program;
                const isCurrent = programId === currentProgram?.id;
                const programHref = `/programs/${programSlug}`;

                return (
                  <li key={programId}>
                    <ProgramCard
                      href={programHref}
                      name={programName}
                      productId={programId}
                      description={programDescription}
                      isCurrent={isCurrent}
                      trackingName={trackingName}
                      logo={
                        <EnterpriseLogo
                          preferRectangle={false}
                          thirdPartyOrganization={organization}
                          program={{ name: programName, metadata: { squareLogo: programSquareLogo } }}
                          logoWidth={LOGO_WIDTH}
                        />
                      }
                      onClick={(e, productId) => onHomepageClick(productId, 'enterprise_program')}
                    />
                  </li>
                );
              })}
              {programs.length > 1 && orgSlug && (
                <li key={`view_all_${orgSlug}_programs`}>
                  <Link
                    href={viewAllProgramHref}
                    variant="quiet"
                    standalone
                    onClick={() => {
                      retracked({
                        trackingName: 'mobile_header_button',
                        action: 'click',
                        trackingData: { name: `View all ${orgName} programs`, href: viewAllProgramHref },
                      });
                      onHomepageClick(orgId, 'third_party_organization');
                    }}
                    css={styles.viewAllLink}
                    typographyVariant="bodySecondary"
                  >
                    {_t('View all #{orgName} programs', { orgName })}
                  </Link>
                </li>
              )}
            </ul>
          </li>
        );
      })}
      {sortedDegrees.map((degree) => {
        const { id: degreeId, name: degreeName, slug: degreeSlug, partners } = degree;

        const isCurrent = degreeId === currentDegree?.id;
        const degreeHref = `/degrees/${degreeSlug}/home`;
        const partnerSquareLogo = partners[0].squareLogo;

        return (
          <li key={degreeId} css={styles.organization}>
            <ProgramCard
              href={degreeHref}
              name={degreeName}
              productId={degreeId}
              isCurrent={isCurrent}
              logo={
                partnerSquareLogo ? (
                  <Imgix
                    src={partnerSquareLogo}
                    maxWidth={LOGO_WIDTH}
                    maxHeight={LOGO_WIDTH}
                    alt={_t('Logo for #{degreeName}', { degreeName })}
                    imgParams={fillWithWhiteBackground}
                  />
                ) : undefined
              }
              onClick={(e, productId) => onHomepageClick(productId, 'degree')}
            />
          </li>
        );
      })}
      <li key="personal-coursera-account" css={styles.organization}>
        <Typography2 component="h3" variant="subtitleMedium" css={styles.orgName}>
          {_t('Browse Coursera')}
        </Typography2>
        <ul css={styles.list} role="none">
          <li>
            <ProgramCard
              href="/"
              name={_t('Coursera')}
              description={_t('All courses')}
              isCurrent={!currentDegree && !currentProgram}
              logo={<Imgix src={CourseraLogo} maxWidth={LOGO_WIDTH} alt={_t('Coursera logo')} />}
              onClick={redirectToMyCoursera}
            />
          </li>
        </ul>
      </li>
    </React.Fragment>
  );
};

export default ProgramList;
